import React, { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { CircularProgress, Box, Alert } from '@mui/material';
import { useAuth } from '../../lib/supabase/AuthContext';
import { getUserRole, checkPermission } from '../../lib/supabase/userService';
import { UserRole } from '../../types/admin';

interface RoleBasedRouteProps {
  requiredRole: UserRole;
  redirectTo?: string;
}

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({ 
  requiredRole, 
  redirectTo = '/' 
}) => {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await getUserRole(user.id);
        
        if (error) {
          throw error;
        }

        setUserRole(data);
      } catch (err: any) {
        console.error('Erreur lors de la récupération du rôle:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user]);

  // Chargement en cours
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '200px' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Erreur lors du chargement du rôle
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Erreur lors de la vérification des permissions: {error}
        </Alert>
      </Box>
    );
  }

  // Pas d'utilisateur connecté
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Pas de rôle trouvé (problème de données)
  if (!userRole) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Aucun rôle trouvé pour cet utilisateur. Contactez l'administrateur.
        </Alert>
      </Box>
    );
  }

  // Vérifier les permissions
  const hasPermission = checkPermission(userRole, requiredRole);

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Accès refusé. Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          <br />
          Votre rôle: {userRole} | Rôle requis: {requiredRole}
        </Alert>
      </Box>
    );
  }

  // L'utilisateur a les bonnes permissions
  return <Outlet />;
};

export default RoleBasedRoute; 